/**
 * Job Service
 * 
 * Handles all job-related operations via API Gateway REST API.
 * This service manages the complete job lifecycle including creation,
 * retrieval, updates, and deletion.
 * 
 * IMPORTANT: Schema matches backend (worker.py, trigger.py)
 * - Uses snake_case attribute names (job_id, job_status, etc.)
 * - Timestamps are Unix milliseconds (created_timestamp, updated_timestamp)
 * - S3 paths are full URIs (s3://bucket/key)
 * 
 * Features:
 * - List jobs with pagination and filtering
 * - Create jobs with automatic TTL configuration
 * - Update job status
 * - Delete jobs
 * - Send job processing messages to SQS
 * 
 * Requirements: 2.1, 5.4, 7.3, 16.4
 */

import { AuthenticatedApiClient } from './authenticated-api-client';
import type { CreationMode, Job, ListJobsOptions, UpdateJobMetadataInput } from '../types';

/**
 * Input for creating a new job
 */
export interface CreateJobInput {
    title: string;
    description?: string;
    author?: string;
    publisher?: string;
    user_id: string;
    environment?: string;
    project_id?: string;
    chapter?: string;
    text_content?: string;
    tts_mode?: string;
    job_mode?: string;
    speaker?: string;
    language?: string;
    instruct?: string;
    ref_text?: string;
    cover_image_s3_path?: string;
    /** Base64-encoded file content for server-side S3 upload */
    file_content_base64?: string;
    /** Base64-encoded cover image for server-side S3 upload */
    cover_image_base64?: string;
    /** MIME type of the cover image (image/jpeg or image/png) */
    cover_image_content_type?: string;
    /** Base64-encoded reference audio for server-side S3 upload */
    ref_audio_base64?: string;
    /** MIME type of the reference audio */
    ref_audio_content_type?: string;
    /** How the job was created: quick, custom, or recipe */
    creation_mode?: CreationMode;
    /** Voice recipe ID used to create this job */
    recipe_id?: string;
    /** Display name of the recipe */
    recipe_name?: string;
    /** Description of the recipe */
    recipe_description?: string;
    /** Market recipe ID used to create this job */
    market_recipe_id?: string;

    /** Generation parameters — all optional, omitted values use model defaults */
    model_choice?: string;
    top_p?: number;
    top_k?: number;
    temperature?: number;
    repetition_penalty?: number;
    max_new_tokens?: number;
    seed?: number;

    /** Dialogue mode fields */
    dialogue_script?: string;
    dialogue_roles?: Array<{
        name: string;
        ref_audio_base64?: string;
        ref_audio_content_type?: string;
        ref_text?: string;
        voice_preset_id?: string;
    }>;
    pause_config?: {
        pause_linebreak: number;
        period_pause: number;
        comma_pause: number;
        question_pause: number;
        hyphen_pause: number;
    };

    /** Voice preset fields */
    save_voice_preset?: boolean;
    voice_preset_name?: string;
    voice_preset_id?: string;

    /** Predicted credit cost from the cost prediction widget */
    predicted_credits?: number;
}

/**
 * Job Service class for managing job operations
 */
export class JobService {
    private client: AuthenticatedApiClient;

    /**
     * Creates a new JobService instance
     * 
     * @throws Error if required environment variables are not set
     */
    constructor() {
        // Validate environment variables
        this.validateEnvironment();

        this.client = new AuthenticatedApiClient();
    }

    /**
     * Validates that required environment variables are set
     * @throws Error if any required environment variable is missing
     */
    private validateEnvironment(): void {
        const required = [
            'VITE_API_GATEWAY_URL',
        ];

        const missing = required.filter(
            (key) => !import.meta.env[key]
        );

        if (missing.length > 0) {
            throw new Error(
                `Missing required environment variables: ${missing.join(', ')}\n` +
                'Please check your .env file and ensure all AWS resource names are configured.'
            );
        }
    }

    /**
     * Lists jobs with optional filtering and pagination
     * 
     * @param options - Query options for filtering and pagination
     * @returns Promise resolving to array of jobs
     * 
     * @example
     * ```typescript
     * // List all jobs
     * const jobs = await jobService.listJobs();
     * 
     * // List completed jobs
     * const completedJobs = await jobService.listJobs({ status: 'completed' });
     * 
     * // Search jobs
     * const searchResults = await jobService.listJobs({ searchQuery: 'test' });
     * 
     * // Paginated results
     * const page1 = await jobService.listJobs({ page: 1, pageSize: 50 });
     * ```
     */
    async listJobs(options?: ListJobsOptions): Promise<{ items: Job[]; total_count: number }> {
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (options?.status) {
                params.append('status', options.status);
            }
            if (options?.searchQuery) {
                params.append('searchQuery', options.searchQuery);
            }
            if (options?.page !== undefined) {
                params.append('page', options.page.toString());
            }
            if (options?.pageSize !== undefined) {
                params.append('pageSize', options.pageSize.toString());
            }
            if (options?.project_id) {
                params.append('project_id', options.project_id);
            }
            if (options?.includeDeleted) {
                params.append('includeDeleted', 'true');
            }
            if (options?.favorited) {
                params.append('favorited', 'true');
            }

            // Call API Gateway endpoint
            const queryString = params.toString();
            const endpoint = queryString ? `/jobs?${queryString}` : '/jobs';
            const response = await this.client.apiRequest(endpoint, { method: 'GET' });

            // Parse response — handle both new { items, total_count } and legacy bare array
            const data = await response.json();
            if (Array.isArray(data)) {
                // Legacy format (bare array) — backward compat during rollout
                return { items: data as Job[], total_count: data.length };
            }
            return { items: data.items as Job[], total_count: data.total_count as number };
        } catch (error) {
            console.error('Error listing jobs:', error);
            throw new Error(
                `Failed to list jobs: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Gets a single job by ID
     * 
     * @param jobId - The job ID to retrieve
     * @returns Promise resolving to the job
     * @throws Error if job is not found
     * 
     * @example
     * ```typescript
     * const job = await jobService.getJob('123e4567-e89b-12d3-a456-426614174000');
     * console.log(job.title, job.job_status);
     * ```
     */
    async getJob(jobId: string): Promise<Job> {
        try {
            // Call API Gateway endpoint
            const response = await this.client.apiRequest(`/jobs/${jobId}`, { method: 'GET' });

            // Parse and return job object
            const job = await response.json();
            return job as Job;
        } catch (error) {
            console.error('Error getting job:', error);
            throw new Error(
                `Failed to get job: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Creates a new job via API Gateway
     * 
     * This method:
     * 1. Sends job data to API Gateway POST /jobs endpoint
     * 2. Backend Lambda generates unique job ID
     * 3. Backend Lambda constructs S3 path matching backend pattern: {job_id}/input.txt
     * 4. Backend Lambda sets TTL to current time + 7 days for automatic cleanup
     * 5. Backend Lambda creates the job record in DynamoDB with backend schema
     * 6. Backend Lambda sends a processing message to the SQS job queue
     * 
     * @param input - Job creation input
     * @returns Promise resolving to the created job
     * 
     * @example
     * ```typescript
     * const job = await jobService.createJob({
     *   title: 'My Test Job',
     *   author: 'John Doe',
     *   user_id: 'user-123',
     * });
     * console.log('Created job:', job.job_id);
     * ```
     */
    async createJob(input: CreateJobInput): Promise<Job> {
        try {
            // Get environment from env variable or default to 'test'
            const environment = import.meta.env.VITE_ENVIRONMENT || 'test';

            // Add environment metadata to job creation.
            // The backend Lambda uses this to tag the DynamoDB record and
            // set TTL to current time + 7 days for automatic test data cleanup.
            const jobData = {
                ...input,
                environment,
                tags: {
                    environment,
                    created_by: 'client-web-app',
                },
            };

            // Call API Gateway endpoint
            const response = await this.client.apiRequest('/jobs', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(jobData),
            });

            // Parse and return created job
            const job = await response.json();
            return job as Job;
        } catch (error) {
            console.error('Error creating job:', error);
            throw new Error(
                `Failed to create job: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Deletes a job via API Gateway
     * 
     * Note: This method only deletes the job record from DynamoDB.
     * Associated files in S3 should be deleted separately using FileService.
     * 
     * @param jobId - The job ID to delete
     * @returns Promise that resolves when deletion is complete
     * 
     * @example
     * ```typescript
     * await jobService.deleteJob('123e4567-e89b-12d3-a456-426614174000');
     * console.log('Job deleted');
     * ```
     */
    async deleteJob(jobId: string): Promise<void> {
        try {
            // Call API Gateway endpoint
            await this.client.apiRequest(`/jobs/${jobId}`, { method: 'DELETE' });
        } catch (error) {
            console.error('Error deleting job:', error);
            throw new Error(
                `Failed to delete job: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Updates the status of a job via API Gateway
     * 
     * @param jobId - The job ID to update
     * @param status - The new status
     * @param progress - Optional progress percentage (0-100)
     * @returns Promise that resolves when update is complete
     * 
     * @example
     * ```typescript
     * // Update to processing with progress
     * await jobService.updateJobStatus('job-123', 'in_progress', 45);
     * 
     * // Update to completed
     * await jobService.updateJobStatus('job-123', 'completed', 100);
     * 
     * // Update to failed
     * await jobService.updateJobStatus('job-123', 'failed');
     * ```
     */
    async updateJobStatus(
        jobId: string,
        status: Job['job_status'],
        progress?: number
    ): Promise<void> {
        try {
            // Build request body
            const body: { status: string; progress?: number } = { status };
            if (progress !== undefined) {
                body.progress = progress;
            }

            // Call API Gateway endpoint
            await this.client.apiRequest(`/jobs/${jobId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });
        } catch (error) {
            console.error('Error updating job status:', error);
            throw new Error(
                `Failed to update job status: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Retries a failed or interrupted job via the backend retry API endpoint
     * 
     * @param jobId - The job ID to retry
     * @returns Promise resolving to the updated job record
     * 
     * @example
     * ```typescript
     * const updatedJob = await jobService.retryJob('job-123');
     * console.log('Job retried, new status:', updatedJob.job_status);
     * ```
     */
    async retryJob(jobId: string): Promise<Job> {
        const response = await this.client.apiRequest(`/jobs/${jobId}/retry`, {
            method: 'POST',
        });
        return response.json();
    }

    /**
     * Toggles the favorite status of a job.
     *
     * @param jobId - The job ID to toggle
     * @param isFavorite - The new favorite state
     */
    async toggleFavorite(jobId: string, isFavorite: boolean): Promise<void> {
        try {
            await this.client.apiRequest(`/jobs/${jobId}/favorite`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ is_favorite: isFavorite }),
            });
        } catch (error) {
            console.error('Error toggling favorite:', error);
            throw new Error(
                `Failed to toggle favorite: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Soft-deletes a job by setting is_deleted=1 via API Gateway.
     * The job record is preserved in DynamoDB but filtered from list views.
     *
     * @param jobId - The job ID to soft-delete
     * @returns Promise that resolves when soft-delete is complete
     *
     * @example
     * ```typescript
     * await jobService.softDeleteJob('123e4567-e89b-12d3-a456-426614174000');
     * ```
     */
    async softDeleteJob(jobId: string): Promise<void> {
        try {
            await this.client.apiRequest(`/jobs/${jobId}/soft-delete`, { method: 'PATCH' });
        } catch (error) {
            console.error('Error soft-deleting job:', error);
            throw new Error(
                `Failed to soft-delete job: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

    /**
     * Updates editable metadata fields on a job via API Gateway.
     * Only the provided fields are sent to the backend — omitted fields remain unchanged.
     *
     * @param jobId - The job ID to update
     * @param input - Object containing only the metadata fields to change
     * @returns Promise resolving to the updated Job record
     *
     * Requirements: 17.4, 17.5
     *
     * @example
     * ```typescript
     * const updated = await jobService.updateJobMetadata('job-123', {
     *   title: 'New Title',
     *   author: 'New Author',
     * });
     * console.log(updated.title); // 'New Title'
     * ```
     */
    async updateJobMetadata(jobId: string, input: UpdateJobMetadataInput): Promise<Job> {
        try {
            const response = await this.client.apiRequest(`/jobs/${jobId}/metadata`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(input),
            });

            const job = await response.json();
            return job as Job;
        } catch (error) {
            console.error('Error updating job metadata:', error);
            throw new Error(
                `Failed to update job metadata: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }

}
